import React, { useState, useEffect } from "react";
import ActualVsProfitForm from "./ActualVsProfitForm";
import { getFinancialProfitDashboard, getMonthlyFinancialData } from "../API's/Actioncreator";
import ActualVsProfitTable from "./ActualVsProfitTable";
import moment from "moment";

const shimmerCSS = `@keyframes shimmer{0%{background-position:-600px 0}100%{background-position:600px 0}}`;
const SkBox = ({ w, h, style = {} }) => (
  <div style={{
    width: w, height: h, borderRadius: "6px",
    background: "linear-gradient(90deg,#ffffff33 25%,#ffffff55 50%,#ffffff33 75%)",
    backgroundSize: "600px 100%",
    animation: "shimmer 1.4s infinite linear",
    ...style,
  }} />
);

const SkeletonForm = () => {
  const isLarge = window.innerWidth > 1800;
  return (
    <div style={{
      background: "#ffffff",
      border: "0.8px solid #E5E7EB",
      borderRadius: "12px",
      overflow: "hidden",
      marginTop: "20px",
      boxShadow: "0px 1px 2px rgba(0,0,0,0.10), 0px 1px 3px rgba(0,0,0,0.10)",
    }}>
      {/* Header */}
      <div style={{
        padding: isLarge ? "18px 24px" : "14px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "0.8px solid #F3F4F6",
      }}>
        <SkBox w="120px" h="20px" />
        <SkBox w="90px" h="24px" style={{ borderRadius: "999px" }} />
      </div>

      {/* Form body */}
      <div style={{ padding: isLarge ? "20px 24px" : "16px 20px" }}>
        <div style={{
          display: "flex", flexWrap: "nowrap", gap: "14px",
          alignItems: "flex-end", overflowX: "auto", paddingBottom: "2px",
        }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ flex: "1 1 160px", minWidth: "140px" }}>
              <SkBox w="70px" h="14px" style={{ marginBottom: "6px" }} />
              <SkBox w="100%" h="40px" style={{ borderRadius: "8px" }} />
            </div>
          ))}
          <div style={{ flexShrink: 0, paddingTop: "19px" }}>
            <SkBox w="110px" h="40px" style={{ borderRadius: "8px" }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ActualVsProfitContainer({ selectedMonth, commonFilter }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(null);
  const [profitLoading, setProfitLoading] = useState(true);
  const [profitTableData, setProfitTableData] = useState(null);
  const [profitError, setProfitError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setLoading(true);
    setFormData(null);
    setProfitLoading(true);
    setProfitTableData(null);
    setProfitError(null);

    if (commonFilter?.fromDate && commonFilter?.toDate) {
      // ── Both APIs expect DD-MM-YYYY format (confirmed from curl examples) ──
      const startDate = moment(commonFilter.fromDate, "YYYY-MM-DD").format("DD-MM-YYYY");
      const endDate   = moment(commonFilter.toDate,   "YYYY-MM-DD").format("DD-MM-YYYY");

      // 1. Pre-fill form with existing monthly data
      getMonthlyFinancialData(
        startDate,
        endDate,
        (response) => {
          if (signal.aborted) return;
          if (response?.data) setFormData(response.data);
          setLoading(false);
        },
        (error) => {
          if (signal.aborted) return;
          setLoading(false); // form shows empty — user can fill manually
        },
        signal,
      );

      // 2. Financial profit dashboard table
      getFinancialProfitDashboard(
        startDate,
        endDate,
        (response) => {
          if (signal.aborted) return;
          setProfitTableData(response);
          setProfitLoading(false);
        },
        (error) => {
          if (signal.aborted) return;
          setProfitError(error);
          setProfitTableData(null);
          setProfitLoading(false);
        },
        signal,
      );
    } else {
      setLoading(false);
      setProfitLoading(false);
    }

    return () => controller.abort();
  }, [selectedMonth, commonFilter?.fromDate, commonFilter?.toDate, refreshTrigger]);

  return (
    <>
      <style>{shimmerCSS}</style>
      {loading ? <SkeletonForm /> : <ActualVsProfitForm selectedMonth={selectedMonth} initialData={formData} onSaveSuccess={handleRefresh} />}
      <ActualVsProfitTable
        loading={profitLoading}
        data={profitTableData}
        error={profitError}
        selectedMonth={selectedMonth}
        commonFilter={commonFilter}
        onSaveSuccess={handleRefresh}
      />
    </>
  );
}
